import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { FaChartLine, FaBriefcase, FaHourglassHalf, FaCheckCircle, FaTabletAlt } from 'react-icons/fa';
import KioskLauncherModal from './KioskLauncherModal';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const SalespersonDashboard = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLauncherOpen, setIsLauncherOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const handleLaunchKiosk = () => {
    setIsLauncherOpen(true);
  };

  const handleLaunchDirect = (sessionToken) => {
    const spName = user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : (user.name || "Salesperson");
    const spId = user._id || user.id || "SP-ACTIVE";
    const tokenParam = sessionToken ? `&kioskToken=${encodeURIComponent(sessionToken)}` : '';
    navigate(`/kiosk?salespersonId=${encodeURIComponent(spId)}&salespersonName=${encodeURIComponent(spName)}${tokenParam}`);
  };

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${getBackendBaseUrl()}/d2-deals`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success) {
          setDeals(data.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch deals", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDeals();
  }, []);

  // Compute stats
  const totalDeals = deals.length;
  const activeDeals = deals.filter(d => !["COMPLETED", "DELIVERED", "FUNDED"].includes(d.status)).length;
  const completedDeals = deals.filter(d => ["COMPLETED", "DELIVERED", "FUNDED"].includes(d.status)).length;

  // Compute graph data
  const statusCounts = deals.reduce((acc, deal) => {
    const status = deal.status || "UNKNOWN";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const chartData = Object.keys(statusCounts).map(status => ({
    name: status.replace(/_/g, " "),
    count: statusCounts[status]
  }));

  if (loading) {
    return (
      <div className="p-8 h-full w-full bg-slate-50/50 flex justify-center items-center">
        <div className="flex flex-col items-center justify-center gap-2">
          <span className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>
          <p className="text-slate-400 text-sm">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 h-full w-full bg-slate-50/50 flex flex-col overflow-y-auto animate-fade-in-up">
      {/* Welcome Area */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2.5 bg-indigo-50 text-indigo-600 rounded-2xl shadow-sm border border-indigo-100">
              <FaChartLine size={24} />
            </span>
            Sales Performance Dashboard
          </h1>
          <p className="text-slate-500 text-xs mt-1 leading-relaxed">
            Monitor active deals, track status breakdowns, and analyze sales performance ratios.
          </p>
        </div>
        <button
          onClick={handleLaunchKiosk}
          className="bg-gradient-to-r from-cyan-600 via-indigo-600 to-blue-600 hover:from-cyan-500 hover:to-indigo-500 text-white font-bold text-xs uppercase tracking-wider px-5 py-3 rounded-2xl shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center gap-2.5 cursor-pointer"
        >
          <FaTabletAlt size={14} className="text-cyan-200" />
          <span>Launch Kiosk Mode</span>
          <span className="bg-white/20 text-[10px] px-2 py-0.5 rounded-full">Auto-Assigned</span>
        </button>
      </div>
      
      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Card 1 */}
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6 flex items-center justify-between hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Structured Deals</p>
            <p className="text-3xl font-extrabold text-slate-800 mt-2">{totalDeals}</p>
          </div>
          <div className="w-12 h-12 bg-blue-50 border border-blue-100 text-blue-600 rounded-xl flex items-center justify-center shadow-sm">
            <FaBriefcase size={18} />
          </div>
        </div>
        
        {/* Card 2 */}
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6 flex items-center justify-between hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Active Workspace Deals</p>
            <p className="text-3xl font-extrabold text-slate-850 mt-2 text-amber-600">{activeDeals}</p>
          </div>
          <div className="w-12 h-12 bg-amber-50 border border-amber-100 text-amber-600 rounded-xl flex items-center justify-center shadow-sm">
            <FaHourglassHalf size={18} />
          </div>
        </div>

        {/* Card 3 */}
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6 flex items-center justify-between hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Completed / Delivered</p>
            <p className="text-3xl font-extrabold text-emerald-600 mt-2">{completedDeals}</p>
          </div>
          <div className="w-12 h-12 bg-emerald-50 border border-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center shadow-sm">
            <FaCheckCircle size={18} />
          </div>
        </div>
      </div>

      {/* Chart Section Container */}
      <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm mb-8">
        <h2 className="text-lg font-bold text-slate-800 mb-6 tracking-tight">Deals Breakdown by Status</h2>
        <div className="h-80 w-full">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 10, left: -20, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366F1" stopOpacity={0.95}/>
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.4}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="name" tick={{ fill: '#94A3B8', fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: '#94A3B8', fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} />
                <Tooltip 
                  cursor={{ fill: '#F8FAFC' }}
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: '1px solid #E2E8F0', 
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)',
                    padding: '8px 12px' 
                  }}
                  itemStyle={{ fontSize: '13px', fontWeight: 600 }}
                  labelStyle={{ fontSize: '11px', color: '#94A3B8', fontWeight: 750, textTransform: 'uppercase', marginBottom: '4px' }}
                />
                <Bar dataKey="count" fill="url(#barGradient)" radius={[6, 6, 0, 0]} barSize={36} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm italic">
              No deals data available for charting.
            </div>
          )}
        </div>
      </div>

      <KioskLauncherModal
        isOpen={isLauncherOpen}
        onClose={() => setIsLauncherOpen(false)}
        onLaunchDirect={handleLaunchDirect}
      />
    </div>
  );
};

export default SalespersonDashboard;
